import { useMutation, useQueryClient } from '@tanstack/react-query';
import { lowerCase } from 'lodash';
import { invalidateAssetDataForNodeChange } from '@/hooks/websocket/mergeAssetPayload';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { renameFolder } from '@/services/folder.service';
import { renameAlbum } from '@/services/album.service';
import { updateAsset } from '@/services/asset.service';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { QueryKey } from '@/lib/queryKeys';
import type { Asset } from '@/types/asset';
import { NodeType } from '@/types/tree';
import { useTranslations } from 'next-intl';

export const useUpdateEntity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const tCommon = useTranslations('common');
  const tErrors = useTranslations('errors');
  const tToasts = useTranslations('toasts');
  const { replaceSlugInUrl } = useDamNavigation();

  const updateAssetWithAuth = useAuthenticatedQueryFn(updateAsset);
  const renameAlbumWithAuth = useAuthenticatedQueryFn(renameAlbum);
  const renameFolderWithAuth = useAuthenticatedQueryFn(renameFolder);

  const assetMutation = useMutation({
    mutationFn: updateAssetWithAuth,
    onSuccess: (updated) => {
      queryClient.setQueryData(['asset', updated.id], (old: Asset) => (old ? { ...old, ...updated } : updated));
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
    },
    onError: (error: any) => {
      /** Another user may have trashed (403) or permanently deleted (404) this asset mid-edit. */
      const assetGone = error?.status === 404 || error?.status === 403;
      if (assetGone) {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
      }

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: assetGone
          ? tErrors('toastEntityDeleted', { resource: 'asset' })
          : (error?.message ?? tErrors('toastDefaultDescription')),
      });
    },
  });

  const albumMutation = useMutation({
    mutationFn: renameAlbumWithAuth,
    onSuccess: (result) => {
      // DM-2520: every album reader (inspector, share create/edit dialog) goes through
      // [QueryKey.Album, id]. The app default is refetchOnMount: false, so invalidating is
      // not enough — a freshly mounted observer would serve the pre-edit name/description
      // until a reload. The PATCH response is the full album, so write it in directly.
      queryClient.setQueryData([QueryKey.Album, result.id], result);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.TreeSearch] });
      void invalidateAssetDataForNodeChange(queryClient);
      replaceSlugInUrl(result.id, result.slug);
    },
  });

  const folderMutation = useMutation({
    mutationFn: renameFolderWithAuth,
    onSuccess: (result) => {
      queryClient.setQueryData([QueryKey.Folder, result.id], result);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.TreeSearch] });
      void invalidateAssetDataForNodeChange(queryClient);
      replaceSlugInUrl(result.id, result.slug);
    },
  });

  /**
   * Update the name and/or description of a folder, album, or asset.
   * Only the keys you pass in `payload` will be sent. `current` is the value
   * before this edit; the success toast only fires when a submitted value
   * actually differs from it, since a disabled Save button is a UI guard and
   * this path can still be reached programmatically. The toast is
   * intentionally generic (not "renamed"/"description updated") because the
   * audit log only ever records an "updated" event, never a rename.
   */
  async function updateEntity(
    kind: NodeType,
    id: string,
    payload: { name?: string; description?: string },
    current: { name?: string; description?: string },
  ): Promise<{ name?: string; description?: string | null }> {
    const result =
      kind === NodeType.Folders
        ? await folderMutation.mutateAsync({ id, ...payload })
        : kind === NodeType.Albums
          ? await albumMutation.mutateAsync({ id, ...payload })
          : await assetMutation.mutateAsync({ id, body: payload });

    const nameChanged = payload.name !== undefined && payload.name !== (current.name ?? '');
    const descriptionChanged =
      payload.description !== undefined && (payload.description ?? '') !== (current.description ?? '');

    if (nameChanged || descriptionChanged) {
      toast({
        title: tToasts('updateSuccessful.title'),
        description: tToasts('updateSuccessful.description', { nodeType: lowerCase(kind?.slice(0, -1)) }),
      });
    }

    return result;
  }

  const isPending = assetMutation.isPending || albumMutation.isPending || folderMutation.isPending;

  return { updateEntity, isPending };
};
